import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { replaceURLPlaceholders } from '@yakit/ui-components';

import { VDLLevel0 } from '@VDL/components/VDLLevel0/VDLLevel0';
import { VDLDefaultIframe } from '@VDL/components/VDLDefaultIframe/VDLDefaultIframe';
import { VDLBanner } from '@VDL/components/VDLBanner/VDLBanner';
import { VDLToolbar } from '@VDL/components/VDLToolbar/VDLToolbar';
import { VDLSidebar } from '@VDL/components/VDLSidebar/VDLSidebar';
import { VDLContainer } from '@VDL/components/VDLContainer';

import { useVDLStore } from '@VDL/store/VDLstore';
import { useVDLModalStore } from '@VDL/store/VDLModalStore';

import { getButtonLog } from '@VDL/api';
import { useFetchAgreement } from '@VDL/hooks/useFetchAgreement';
import { useUpdateURLWithPattern } from '@VDL/hooks/useUpdateURLWithPattern';

import { INITIAL_PAGE_STATE } from '@VDL/utils/consts';

import type { IVDLButton } from '@VDL/api/types/IVDLButton';
import type { TVDLLayoutState } from '@VDL/types/VDLLayoutState';
import type { VDLLevel0Type } from '@VDL/types/VDLLevel0Type';
import { HeaderMFWrapper } from '@/components/HeaderMFWrapper';

export const HomeView = () => {
  useFetchAgreement();

  const [pageState, setPageState] = useState(INITIAL_PAGE_STATE);
  const [tableType, setTableType] = useState<VDLLevel0Type>('graph');
  const [isEditGraph, setIsEditGraph] = useState(false);
  const { level0Code } = useParams();
  const { selectedYear } = useVDLStore();
  const { setDefaultIframe } = useVDLModalStore();

  const { updatedURL } = useUpdateURLWithPattern({
    unresolvedURL: pageState.unresolvedURL,
    level0Code,
    selectedYear
  });

  const onClickButtonObject = (buttonObject: IVDLButton) => {
    const type = buttonObject.tip;
    const unresolvedURL = buttonObject.parms;
    const resolvedURL = replaceURLPlaceholders(unresolvedURL ?? '', {
      lvl_0: level0Code,
      year: selectedYear
    });

    getButtonLog(buttonObject.code)

    let newState: TVDLLayoutState | undefined;

    switch (type) {
      case 'modal':
        setDefaultIframe({
          type: 'default',
          title: buttonObject.name ?? '',
          src: resolvedURL
        });
        newState = INITIAL_PAGE_STATE;
        break;
      case 'iframe':
        newState = {
          code: buttonObject.code,
          type: 'iframe',
          title: buttonObject.name ?? '',
          resolvedURL,
          unresolvedURL: unresolvedURL ?? '',
          isEdit: null,
          haveBackButton: true
        };
        break;
      case 'callback':
        newState = {
          code: buttonObject.code,
          type: 'event_plan',
          title: 'План мероприятий',
          resolvedURL: null,
          unresolvedURL: null,
          isEdit: false,
          haveBackButton: true
        };
        break;
      case 'link':
        window.open(resolvedURL, '_blank');
        break;
      case 'request':
        if (resolvedURL) {
          const a = document.createElement('a');
          a.href = resolvedURL;
          a.download = '';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        newState = INITIAL_PAGE_STATE;
        break;
    }

    setPageState((prev) => {
      if (!newState) return INITIAL_PAGE_STATE;
      if (prev.code === newState.code) return INITIAL_PAGE_STATE;
      return newState;
    });
  };

  return (
    <div className="relative flex min-h-dvh flex-col">
      <HeaderMFWrapper />
      <VDLContainer className="shrink-0 md:mt-4">
        <VDLBanner
          title="Показатели оценки эффективности деятельности высших должностных лиц"
          onClickButtonObject={onClickButtonObject}
          activeButtonObjectCode={pageState.code}
        />
      </VDLContainer>
      <VDLContainer className="relative flex min-h-[37.5rem] flex-1 flex-col gap-6 px-6 py-6">
        <VDLToolbar
          isEdit={isEditGraph}
          editLabel="Рейтинг"
          // isHaveEdit={tableType === 'graph'}
          onToggleEdit={(value) => setIsEditGraph(value)}
          activeTableButtonCode={tableType}
          onClickTableButton={(type) => setTableType(type)}
        />
        <div className="absolute top-0 left-6 h-full flex-1">
          <VDLSidebar />
        </div>
        <div className="relative flex flex-1 flex-col">
          {pageState.type === 'default' && (
            <VDLLevel0 type={tableType} isEditGraph={isEditGraph} />
          )}
          {pageState.type === 'iframe' && (
            <VDLDefaultIframe src={updatedURL ?? pageState.resolvedURL} />
          )}
        </div>
      </VDLContainer>
    </div>
  );
};
